import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card, DatePicker, Descriptions, Drawer, Input, Select, Space, Tag, Typography } from '@/shared/ui/primitives';
import { ClearOutlined, EyeOutlined, ReloadOutlined } from '@/shared/ui/icons';
import PageHeader from '@/shared/components/PageHeader';
import { formatDateTime } from '@/shared/utils/formatters';
import { useAnalyticsEvents, useAnalyticsEventsSummary, useAnalyticsGeoCities, useAnalyticsGeoDistricts } from '../hooks/useAnalyticsQueries';
import type { AnalyticsEvent } from '../api/analytics.api';

const { RangePicker } = DatePicker;

const COMMON_EVENT_NAMES = [
  'session_started',
  'app_opened',
  'App opened',
  'map_moved',
  'Map moved',
  'screen_viewed',
  'room_ad_viewed',
  'room_wanted_viewed',
  'search_result_clicked',
  'search_performed',
  'room_ad_contacted',
  'room_wanted_contacted',
  'contact_attempted',
  'contact_completed',
  'whatsapp_contact_clicked',
  'call_clicked',
  'chat_conversation_started',
  'listing_room_ad_published',
  'listing_room_wanted_published',
  'auth_otp_requested',
  'auth_otp_verified',
  'auth_signup_completed',
  'auth_login_completed',
  'login_completed',
];

function eventTone(eventName?: string | null) {
  const name = String(eventName ?? '');
  if (name.includes('contact') || name.includes('whatsapp') || name.includes('call')) return 'green';
  if (name.includes('view')) return 'blue';
  if (name.includes('search')) return 'purple';
  if (name.includes('publish') || name.includes('created')) return 'gold';
  return 'geekblue';
}

function contactMethod(row: AnalyticsEvent) {
  return String(row.metadata?.contact_method ?? row.metadata?.method ?? '-');
}

type Translate = (key: string, options?: any) => string;

function eventLabel(eventName: string | null | undefined, t: Translate) {
  const key = String(eventName ?? 'unknown');
  const translated = t(`analytics.events.eventNames.${key}`, { defaultValue: key });
  return translated || key;
}

function entityLabel(entityType: string | null | undefined, t: Translate) {
  if (!entityType) return t('analytics.events.notLinked');
  return t(`analytics.events.entityTypes.${entityType}`, { defaultValue: entityType });
}

function contactMethodLabel(value: string, t: Translate) {
  if (!value || value === '-') return t('analytics.events.noContactMethod');
  return t(`analytics.events.contactMethods.${value}`, { defaultValue: value });
}

function targetLabel(row: AnalyticsEvent) {
  if (row.targetAdTitle) return row.targetAdTitle;
  if (row.entityType && row.entityId) return `${row.entityType} #${row.entityId}`;
  return '-';
}

function entityLink(row: AnalyticsEvent) {
  if (!row.entityType || !row.entityId) return null;
  if (row.entityType === 'room_ad') return `/room-ads/${row.entityId}`;
  if (row.entityType === 'room_wanted') return `/room-wanted/${row.entityId}`;
  return null;
}

function userLabel(row: AnalyticsEvent, anonymous: string) {
  if (!row.userId) return anonymous;
  return `${row.userName || `#${row.userId}`} (#${row.userId})`;
}

function ownerLabel(row: AnalyticsEvent) {
  if (!row.targetUserId) return null;
  return `${row.targetUserName || `#${row.targetUserId}`} (#${row.targetUserId})`;
}

function isListingEvent(row: AnalyticsEvent) {
  return row.entityType === 'room_ad' || row.entityType === 'room_wanted' || Boolean(row.targetAdTitle || row.targetUserId);
}

function Stat({ title, value, hint }: { title: string; value: any; hint?: string }) {
  return (
    <div className="ds-stat">
      <div className="ds-stat-head"><span>{title}</span></div>
      <div className="ds-stat-value">{value ?? 0}</div>
      {hint ? <div className="ds-stat-hint">{hint}</div> : null}
    </div>
  );
}

function MetricGrid({ children }: { children: React.ReactNode }) {
  return <div className="ds-row" style={{ gap: 16, marginInline: -8 }}>{children}</div>;
}

function MetricCell({ children }: { children: React.ReactNode }) {
  return <div style={{ flex: '1 1 220px', padding: 8 }}>{children}</div>;
}

export default function AnalyticsEventsPage() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(0);
  const [size] = useState(25);
  const [filters, setFilters] = useState<any>({});
  const [detail, setDetail] = useState<AnalyticsEvent | null>(null);

  const params = { page, size, ...filters };
  const { data, isLoading, isFetching, refetch } = useAnalyticsEvents(params);
  const summary = useAnalyticsEventsSummary(filters);
  const cities = useAnalyticsGeoCities();
  const districts = useAnalyticsGeoDistricts(filters.cityId);
  const rows = data?.content ?? [];

  const metrics = useMemo(() => {
    const uniqueUsers = new Set(rows.map((row) => row.userId).filter(Boolean)).size;
    const contacts = rows.filter((row) => String(row.eventName ?? '').includes('contact') || String(row.eventName ?? '').includes('whatsapp') || String(row.eventName ?? '').includes('call')).length;
    const views = rows.filter((row) => String(row.eventName ?? '').includes('view')).length;
    const linkedTargets = rows.filter((row) => row.targetUserId || row.targetAdTitle).length;
    return { uniqueUsers, contacts, views, linkedTargets };
  }, [rows]);

  const cityOptions = useMemo(
    () => (cities.data ?? []).map((city) => ({ value: city.id, label: city.name || `#${city.id}` })),
    [cities.data],
  );

  const districtOptions = useMemo(
    () => (districts.data ?? []).map((district) => ({ value: district.id, label: district.name || `#${district.id}` })),
    [districts.data],
  );

  const eventOptions = useMemo(
    () => COMMON_EVENT_NAMES.map((value) => ({ value, label: `${eventLabel(value, t)} (${value})` })),
    [t, i18n.language],
  );

  const setFilter = (patch: any) => {
    setFilters((prev: any) => {
      const next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') delete next[key];
        else next[key] = value;
      });
      return next;
    });
    setPage(0);
  };

  const linkedFilter = (patch: any, label: string) => (
    <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontWeight: 700 }} onClick={() => setFilter(patch)}>
      {label}
    </Button>
  );

  return (
    <div>
      <PageHeader
        title={t('analytics.events.title')}
        subtitle={t('analytics.events.subtitle')}
      />

      <Card className="analytics-filter-card" styles={{ body: { padding: 16 } }} style={{ marginBottom: 16 }}>
        <div className="analytics-filter-header">
          <div>
            <strong>{t('analytics.events.filtersTitle')}</strong>
            <div className="ds-muted">{t('analytics.events.filtersSubtitle')}</div>
          </div>
          <Space wrap>
            <Button icon={<ClearOutlined />} onClick={() => { setFilters({}); setPage(0); }}>{t('common.reset')}</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>{t('common.refresh')}</Button>
          </Space>
        </div>
        <div className="analytics-filter-grid">
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.userId')}</span>
            <Input value={filters.userId ?? ''} type="number" onChange={(e: any) => setFilter({ userId: e.target.value || undefined })} />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.userSearch')}</span>
            <Input value={filters.userSearch ?? ''} onChange={(e: any) => setFilter({ userSearch: e.target.value || undefined })} />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.eventName')}</span>
            <Select
              allowClear
              showSearch
              value={filters.eventName}
              options={eventOptions}
              placeholder={t('analytics.events.placeholders.eventName')}
              onChange={(value) => setFilter({ eventName: value })}
            />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.screenName')}</span>
            <Input value={filters.screenName ?? ''} placeholder={t('analytics.events.placeholders.screenName')} onChange={(e: any) => setFilter({ screenName: e.target.value || undefined })} />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.entityType')}</span>
            <Select
              allowClear
              value={filters.entityType}
              options={[
                { value: 'room_ad', label: t('analytics.common.roomAds') },
                { value: 'room_wanted', label: t('analytics.common.roomWanted') },
              ]}
              onChange={(value) => setFilter({ entityType: value })}
            />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.entityId')}</span>
            <Input value={filters.entityId ?? ''} type="number" onChange={(e: any) => setFilter({ entityId: e.target.value || undefined })} />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.common.platform')}</span>
            <Select
              allowClear
              value={filters.platform}
              options={['ios', 'android', 'web'].map((value) => ({ value, label: value }))}
              onChange={(value) => setFilter({ platform: value })}
            />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.common.cityKey')}</span>
            <Select
              allowClear
              showSearch
              loading={cities.isLoading}
              value={filters.cityId}
              options={cityOptions}
              placeholder={t('analytics.filters.allCities')}
              onChange={(value) => setFilter({ cityId: value, districtId: undefined })}
            />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.common.districtKey')}</span>
            <Select
              allowClear
              showSearch
              disabled={!filters.cityId}
              loading={districts.isLoading}
              value={filters.districtId}
              options={districtOptions}
              placeholder={filters.cityId ? t('analytics.filters.allDistricts') : t('analytics.filters.selectCityFirst')}
              onChange={(value) => setFilter({ districtId: value })}
            />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.campaign')}</span>
            <Input value={filters.campaign ?? ''} onChange={(e: any) => setFilter({ campaign: e.target.value || undefined })} />
          </label>
          <label className="analytics-filter-field">
            <span>{t('analytics.events.filters.range')}</span>
            <RangePicker
              showTime
              value={[filters.createdFrom ?? null, filters.createdTo ?? null]}
              onChange={(vals: any) => {
                setFilter({
                  createdFrom: vals?.[0]?.toISOString(),
                  createdTo: vals?.[1]?.toISOString(),
                });
              }}
            />
          </label>
        </div>
      </Card>

      <MetricGrid>
        <MetricCell><Stat title={t('analytics.events.metrics.total')} value={summary.data?.totalMatchingEvents ?? data?.totalItems ?? 0} hint={t('analytics.events.metrics.totalHint')} /></MetricCell>
        <MetricCell><Stat title={t('analytics.events.metrics.users')} value={metrics.uniqueUsers} hint={t('analytics.events.metrics.pageHint')} /></MetricCell>
        <MetricCell><Stat title={t('analytics.events.metrics.contacts')} value={metrics.contacts} hint={t('analytics.events.metrics.pageHint')} /></MetricCell>
        <MetricCell><Stat title={t('analytics.events.metrics.views')} value={metrics.views} hint={t('analytics.events.metrics.pageHint')} /></MetricCell>
      </MetricGrid>

      <Card title={t('analytics.events.topUsersTitle')} style={{ marginTop: 16 }}>
        {summary.isLoading ? (
          <div className="ds-muted">{t('common.loading')}</div>
        ) : !summary.data?.topUsers?.length ? (
          <div className="ds-muted">{t('analytics.events.noTopUsers')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {summary.data.topUsers.map((user, index) => (
              <div key={user.userId} style={{ display: 'flex', gap: 10, alignItems: 'center', borderBottom: '1px solid var(--ds-border)', paddingBottom: 10, flexWrap: 'wrap' }}>
                <strong style={{ color: 'var(--ds-primary)' }}>{index + 1}</strong>
                <div style={{ flex: '1 1 220px' }}>
                  <Link to={`/users/${user.userId}`} style={{ fontWeight: 700 }}>{user.userName || `#${user.userId}`}</Link>
                  <div className="ds-muted">{user.userEmail || user.userPhone || `#${user.userId}`}</div>
                </div>
                <Tag color="green">{t('analytics.events.metrics.contacts')}: {user.contactCount}</Tag>
                <Tag color="purple">{t('analytics.events.metrics.searches')}: {user.searchCount}</Tag>
                <Tag color="blue">{t('analytics.events.metrics.views')}: {user.viewCount}</Tag>
                <Tag>{t('analytics.events.metrics.total')}: {user.totalEvents}</Tag>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={t('analytics.events.activityTitle')} style={{ marginTop: 16 }}>
        {isLoading ? (
          <div className="ds-muted">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="ds-muted">{t('analytics.empty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {rows.map((row) => (
              <div key={row.id} style={{ border: '1px solid var(--ds-border)', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div>
                    <Tag color={eventTone(row.eventName)}>{eventLabel(row.eventName, t)}</Tag>
                    <strong style={{ marginInlineStart: 8 }}>{userLabel(row, t('analytics.events.anonymous'))}</strong>
                    <div className="ds-muted" style={{ marginTop: 6 }}>{formatDateTime(row.createdAt || row.occurredAt || '')}</div>
                  </div>
                  <Button type="link" icon={<EyeOutlined />} onClick={() => setDetail(row)}>{t('common.view')}</Button>
                </div>
                <div className="ds-row" style={{ gap: 12, marginTop: 12 }}>
                  {isListingEvent(row) ? (
                    <>
                      <div style={{ flex: '1 1 260px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.targetAd')}</div>
                        {entityLink(row) ? (
                          <Link to={entityLink(row) as string} style={{ fontWeight: 700 }}>{targetLabel(row)}</Link>
                        ) : (
                          <strong>{targetLabel(row)}</strong>
                        )}
                      </div>
                      <div style={{ flex: '1 1 220px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.targetOwner')}</div>
                        {ownerLabel(row) ? <Link to={`/users/${row.targetUserId}`} style={{ fontWeight: 700 }}>{ownerLabel(row)}</Link> : <strong>{t('analytics.events.ownerUnavailable')}</strong>}
                      </div>
                      <div style={{ flex: '1 1 160px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.contactMethod')}</div>
                        {contactMethod(row) !== '-'
                          ? linkedFilter({ eventName: row.eventName }, contactMethodLabel(contactMethod(row), t))
                          : <strong>{contactMethodLabel(contactMethod(row), t)}</strong>}
                      </div>
                      <div style={{ flex: '1 1 180px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.entity')}</div>
                        {entityLink(row) ? (
                          <Link to={entityLink(row) as string} style={{ fontWeight: 700 }}>{entityLabel(row.entityType, t)}{row.entityId ? ` #${row.entityId}` : ''}</Link>
                        ) : (
                          <strong>{entityLabel(row.entityType, t)}{row.entityId ? ` #${row.entityId}` : ''}</strong>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ flex: '1 1 240px' }}>
                        <div className="ds-muted">{t('analytics.events.fields.screen')}</div>
                        {row.screenName ? linkedFilter({ screenName: row.screenName }, row.screenName) : <strong>{t('analytics.events.noScreen')}</strong>}
                      </div>
                      <div style={{ flex: '1 1 240px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.session')}</div>
                        {row.sessionId ? linkedFilter({ sessionId: row.sessionId }, row.sessionId) : <strong>{t('analytics.events.noSession')}</strong>}
                      </div>
                      <div style={{ flex: '1 1 200px' }}>
                        <div className="ds-muted">{t('analytics.events.columns.entity')}</div>
                        <strong>{t('analytics.events.notLinked')}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <span className="ds-muted">{t('analytics.events.total', { total: data?.totalItems ?? 0 })}</span>
          <Space>
            <Button disabled={page <= 0 || isFetching} onClick={() => setPage((current) => Math.max(0, current - 1))}>{t('common.previous')}</Button>
            <Tag>{page + 1} / {Math.max(data?.totalPages ?? 1, 1)}</Tag>
            <Button disabled={page + 1 >= (data?.totalPages ?? 1) || isFetching} onClick={() => setPage((current) => current + 1)}>{t('common.next')}</Button>
          </Space>
        </div>
      </Card>

      <Drawer width={620} open={!!detail} onClose={() => setDetail(null)} title={detail ? t('analytics.events.detailTitle', { id: detail.id }) : ''}>
        {detail && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={t('analytics.events.columns.event')}><Tag color={eventTone(detail.eventName)}>{eventLabel(detail.eventName, t)}</Tag></Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.rawEvent')}>{detail.eventName || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.eventId')}><Typography.Text copyable>{detail.eventId || '-'}</Typography.Text></Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.user')}>{userLabel(detail, t('analytics.events.anonymous'))}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.userEmail')}>{detail.userEmail || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.userPhone')}>{detail.userPhone || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.session')}>
              {detail.sessionId ? linkedFilter({ sessionId: detail.sessionId }, detail.sessionId) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.screen')}>
              {detail.screenName ? linkedFilter({ screenName: detail.screenName }, detail.screenName) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.entity')}>
              {entityLink(detail) ? <Link to={entityLink(detail) as string}>{entityLabel(detail.entityType, t)} #{detail.entityId}</Link> : detail.entityType ? `${entityLabel(detail.entityType, t)} #${detail.entityId ?? '-'}` : t('analytics.events.notLinked')}
            </Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.targetOwner')}>
              {ownerLabel(detail) ? <Link to={`/users/${detail.targetUserId}`}>{ownerLabel(detail)}</Link> : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.targetAd')}>
              {entityLink(detail) ? <Link to={entityLink(detail) as string}>{detail.targetAdTitle || targetLabel(detail)}</Link> : detail.targetAdTitle || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.contactMethod')}>{contactMethodLabel(contactMethod(detail), t)}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.location')}>{detail.cityId ? `${t('analytics.common.cityKey')} ${detail.cityId}` : '-'}{detail.districtId ? ` / ${t('analytics.common.districtKey')} ${detail.districtId}` : ''}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.common.platform')}>{detail.platform || '-'} / {detail.appVersion || '-'}</Descriptions.Item>
            <Descriptions.Item label="AppsFlyer">{detail.appsflyerId || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.filters.campaign')}>{detail.mediaSource || '-'} / {detail.campaign || '-'} / {detail.adset || '-'} / {detail.ad || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.deepLink')}>{detail.deepLinkValue || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.retargeting')}>{detail.isRetargeting == null ? '-' : detail.isRetargeting ? t('common.yes') : t('common.no')}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.occurred')}>{detail.occurredAt ? formatDateTime(detail.occurredAt) : '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.columns.created')}>{detail.createdAt ? formatDateTime(detail.createdAt) : '-'}</Descriptions.Item>
            <Descriptions.Item label={t('analytics.events.fields.metadata')}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {detail.metadata ? JSON.stringify(detail.metadata, null, 2) : '-'}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
